"use client";

import { useRef } from "react";

import { revalidateLogic } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { useTranslations } from "next-intl";

import type { ServiceTemplateFieldDefinition } from "@louez/types";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@louez/ui";

import { RootError } from "@/components/form/root-error";
import { useAppForm } from "@/hooks/form/form";
import { getFieldError } from "@/hooks/form/form-context";
import { storefrontServicesQueries } from "@/lib/queries/services.queries";

import { ConfiguredServiceField } from "./configured-service-field";
import { QuoteEstimateResult } from "./quote-estimate-result";
import { QuoteOptionalLines } from "./quote-optional-lines";
import { QuoteRequestSuccess } from "./quote-request-success";
import {
  getServiceQuoteDefaults,
  getServiceQuoteSchema,
  isServiceFieldVisible,
} from "./util.service-quote-form";

type PublicTemplate = {
  fields: ServiceTemplateFieldDefinition[];
  lines: {
    code: string;
    defaultEnabled: boolean;
    description: string;
    optional: boolean;
  }[];
};

type ConfiguredServiceFormProps = {
  accountHref: string;
  currency: string;
  serviceId: string;
  servicesHref: string;
  template: PublicTemplate;
};

export const ConfiguredServiceForm = ({
  accountHref,
  currency,
  serviceId,
  servicesHref,
  template,
}: ConfiguredServiceFormProps) => {
  const t = useTranslations("storefront.services");
  const submitIntent = useRef<"evaluate" | "quote">("evaluate");
  const evaluateMutation = useMutation(storefrontServicesQueries.evaluate());
  const quoteMutation = useMutation(storefrontServicesQueries.requestQuote());
  const optionalLines = template.lines.filter((line) => line.optional);
  const form = useAppForm({
    defaultValues: {
      ...getServiceQuoteDefaults(template.fields),
      enabledOptionalLineCodes: optionalLines
        .filter((line) => line.defaultEnabled)
        .map((line) => line.code),
    },
    validationLogic: revalidateLogic({
      mode: "submit",
      modeAfterSubmission: "change",
    }),
    validators: {
      onSubmit: getServiceQuoteSchema(template.fields),
    },
    onSubmit: async ({ value }) => {
      const evaluationInput = {
        id: serviceId,
        configuration: value.configuration,
        enabledOptionalLineCodes: value.enabledOptionalLineCodes,
      };
      await evaluateMutation.mutateAsync(evaluationInput);

      if (submitIntent.current === "quote") {
        await quoteMutation.mutateAsync({
          ...evaluationInput,
          name: value.name,
          email: value.email,
          phone: value.phone || null,
          notes: value.notes || null,
        });
      }
      submitIntent.current = "evaluate";
    },
  });

  if (quoteMutation.data) {
    return (
      <QuoteRequestSuccess
        accountHref={accountHref}
        currency={currency}
        onRequestAnother={() => {
          quoteMutation.reset();
          evaluateMutation.reset();
          form.reset();
          submitIntent.current = "evaluate";
        }}
        reservationNumber={quoteMutation.data.reservationNumber}
        servicesHref={servicesHref}
        total={quoteMutation.data.total}
      />
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{t("configureService")}</CardTitle>
        <p className="text-muted-foreground text-sm">
          {t("configureServiceHint")}
        </p>
      </CardHeader>
      <CardContent>
        <form.AppForm>
          <form.Form className="space-y-6" formName="service-quote">
            <RootError
              error={
                quoteMutation.error?.message ??
                evaluateMutation.error?.message ??
                null
              }
            />

            <fieldset className="space-y-3">
              <legend className="font-semibold">{t("serviceDetails")}</legend>
              <form.Subscribe
                selector={(state) => state.values.configuration}
              >
                {(configuration) => {
                  const visibleFields = template.fields.filter((field) =>
                    isServiceFieldVisible(field, configuration),
                  );
                  const inputFields = visibleFields.filter(
                    (field) => field.type !== "checkbox",
                  );
                  const checkboxFields = visibleFields.filter(
                    (field) => field.type === "checkbox",
                  );

                  return (
                    <div className="space-y-4">
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {inputFields.map((templateField) => (
                          <form.Field
                            key={templateField.key}
                            name={`configuration.${templateField.key}`}
                          >
                            {(field) => (
                              <ConfiguredServiceField
                                compact
                                error={
                                  field.state.meta.errors[0]
                                    ? getFieldError(field.state.meta.errors[0])
                                    : undefined
                                }
                                field={templateField}
                                value={field.state.value}
                                onBlur={field.handleBlur}
                                onChange={(value) => field.handleChange(value)}
                              />
                            )}
                          </form.Field>
                        ))}
                      </div>
                      {checkboxFields.length > 0 && (
                        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                          {checkboxFields.map((templateField) => (
                            <form.Field
                              key={templateField.key}
                              name={`configuration.${templateField.key}`}
                            >
                              {(field) => (
                                <ConfiguredServiceField
                                  compact
                                  error={
                                    field.state.meta.errors[0]
                                      ? getFieldError(
                                          field.state.meta.errors[0],
                                        )
                                      : undefined
                                  }
                                  field={templateField}
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={(value) =>
                                    field.handleChange(value)
                                  }
                                />
                              )}
                            </form.Field>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }}
              </form.Subscribe>
            </fieldset>

            <form.AppField name="enabledOptionalLineCodes">
              {(field) => (
                <QuoteOptionalLines
                  enabledCodes={field.state.value}
                  lines={optionalLines}
                  onChange={field.handleChange}
                />
              )}
            </form.AppField>

            <fieldset className="space-y-3 border-t pt-5">
              <legend className="font-semibold">{t("contactDetails")}</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <form.AppField name="name">
                  {(field) => <field.Input label={t("name")} />}
                </form.AppField>
                <form.AppField name="email">
                  {(field) => (
                    <field.Input label={t("email")} type="email" />
                  )}
                </form.AppField>
                <form.AppField name="phone">
                  {(field) => <field.Input label={t("phone")} type="tel" />}
                </form.AppField>
                <form.AppField name="notes">
                  {(field) => (
                    <field.Textarea label={t("notes")} rows={2} />
                  )}
                </form.AppField>
              </div>
            </fieldset>

            {evaluateMutation.data && (
              <QuoteEstimateResult
                currency={currency}
                result={evaluateMutation.data}
              />
            )}

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <form.SubscribeButton
                className="sm:min-w-44"
                onClick={() => {
                  submitIntent.current = "evaluate";
                }}
              >
                {t("calculateQuote")}
              </form.SubscribeButton>
              <Button
                className="sm:min-w-44"
                type="submit"
                disabled={
                  quoteMutation.isPending || evaluateMutation.isPending
                }
                onClick={() => {
                  submitIntent.current = "quote";
                }}
              >
                {t("requestQuote")}
              </Button>
            </div>
          </form.Form>
        </form.AppForm>
      </CardContent>
    </Card>
  );
};
